import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import backgroundImage from "./assets/bg-image.png";
import titleRobo from "./assets/title.jpg";
import section1 from "./assets/section-1.png";
import roboShare from "./assets/robo-share.png";
import roboComment from "./assets/robo-comment.png";
import theme from "./assets/theme.png";
import googlePlayBanner from "./assets/google-play-banner.png";
import appStoreBanner from "./assets/app-store-banner.png";
import iconLock from "./assets/icon-lock.png";
import iconBackup from "./assets/icon-backup.png";
import iconSearch from "./assets/icon-search.png";
import iconFree from "./assets/icon-free.png";

import summary from "./assets/summary.png";
import "./TopPage.scss";
import { Helmet } from "react-helmet";

import ReactGA from "react-ga4";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=net.aidiary";
const APP_STORE_URL = "https://apps.apple.com/jp/app/id6736706558";

function TopPage() {
  const navigate = useNavigate();
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const page = query.get("page");
  const tutorial = query.get("tutorial");
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set(),
  );
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: TopPage.name,
      title: TopPage.name,
    });
  }, []);

  useEffect(() => {
    // /?page=pp の場合 /ppにリダイレクト
    if (page) {
      console.log("page", page);
      navigate("/" + page);
    }
  }, [navigate, page, query]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionRefs.current.forEach((section, index) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -50px 0px",
        },
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div
      className="TopPage-wrap"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        backgroundSize: "auto",
      }}
    >
      <div
        className="App"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "repeat",
          backgroundPosition: "0 0",
          backgroundSize: "auto",
        }}
      >
        <Helmet>
          <title>AI日記 キロノ：かわいい、簡単、つながる。新しい日記習慣</title>
          <meta
            name="description"
            content="AIサポートにより、プロ並みの日記が最短1分で書ける日記アプリ！交換日記・自動バックアップ機能付き！パターンロックでセキュリティも万全！完全無料！"
          />
          <meta http-equiv="Content-Script-Type" content="text/javascript" />
          <meta http-equiv="Content-Style-Type" content="text/css" />

          <meta
            property="og:title"
            content="AI日記 キロノ：かわいい、簡単、つながる。新しい日記習慣"
          />
          <meta
            property="og:description"
            content="「大切な日々を美しく残したい！だけど忙しくて時間がない！」そんなあなたへ。素敵な画像付き日記をAIが代筆します！パターンロックや交換日記など、その他の機能も充実！"
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.ai-diary.jp/" />
          <meta
            property="og:image"
            content="https://www.ai-diary.jp/image/thumbnail.png"
          />
          <meta
            property="og:site_name"
            content="AI日記 キロノ：かわいい、簡単、つながる。新しい日記習慣"
          />
          <meta property="og:locale" content="ja_JP" />

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=M+PLUS+2:wght@100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Nobile:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&family=Noto+Sans+JP:wght@100..900&family=Orbitron:wght@400..900&family=Petrona:ital,wght@0,100..900;1,100..900&family=Zen+Maru+Gothic&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        {tutorial !== "true" && (
          <div className="language-area">
            <a href="/">日本語</a>
            <p>/</p>
            <a href="/en">English</a>
            <p>/</p>
            <a href="/zh">中文</a>
          </div>
        )}
        <div
          className="title-area"
          style={{
            backgroundImage: `url(${titleRobo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <p className="sub-title">
            三日坊主でも大丈夫！
            <br />
            可愛い執事「キロノ」がお手伝い！
            <br />
            生成AI搭載 日記アプリ
          </p>
        </div>
        {tutorial !== "true" && (
          <div className="store-image-wrapper">
            <img
              src={googlePlayBanner}
              onClick={() => {
                window.open(GOOGLE_PLAY_URL, "_blank");
              }}
              className="google-playbanner-image"
              alt="google play"
            />
            <img
              src={appStoreBanner}
              onClick={() => {
                window.open(APP_STORE_URL, "_blank");
              }}
              className="google-playbanner-image"
              alt="App Store"
            />
          </div>
        )}
        <div
          className={`section scroll-animate ${
            visibleSections.has(0) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <TitleLabel
            label="特徴"
            number="①"
            sub="あなたの毎日が輝き出す！"
            title="AIによる日記作成機能"
          />
          <img
            src={section1}
            className="section-image trim-vertical"
            alt="section1"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                AIと楽しくチャットしたり、簡単な質問に答えるだけで、日記を代筆します
              </li>
              <li>日記の内容から、素敵な画像も生成できます</li>
              <li>
                AIボイスによる読み上げ機能で、まるで物語の朗読を
                <br />
                聞いているような体験ができます
              </li>
            </ul>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(1) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <TitleLabel
            label="特徴"
            number="②"
            sub="コメントがもらえる！"
            title="AIがあなたの日記にコメントしてくれます"
          />
          <img src={roboComment} className="section-image" alt="section1" />
          <div className="description-wrapper">
            <ul>
              <li>
                AIキャラからの日記に対するコメントを貰えるので、日記を続けるモチベーションになります。
              </li>
              <li>
                悩んでいるときは気持ちを切り替えるアドバイスをくれるかも!?
              </li>
            </ul>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(2) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <TitleLabel
            label="特徴"
            number="③"
            sub="交換日記も簡単！"
            title="お友達やみんなと日記をシェア"
          />
          <img src={roboShare} className="section-image" alt="section1" />
          <div className="description-wrapper">
            <ul>
              <li>日記をお友達やみんなに公開することも可能</li>
              <li>公開範囲は限定できるからプライバシーもしっかり保護</li>
            </ul>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(3) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <TitleLabel
            label="特徴"
            number="④"
            sub="もっと自分らしい日記帳に！"
            title="フォント&背景をカスタマイズ"
          />
          <img src={theme} className="section-image trim-top" alt="section1" />
          <div className="description-wrapper">
            <ul>
              <li>
                フォント、文字色、文字サイズ、背景色、背景画像など、日記のデザインを自分好みにカスタマイズできます。
              </li>
            </ul>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(4) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑤"
            sub="毎週の振り返りも簡単！"
            title="一週間のまとめ機能"
          />
          <div className="section-image-description-row">
            <img src={summary} className="left-image" alt="section1" />
            <div className="description-wrapper">
              <ul>
                <li>
                  一週間に3回以上日記を書くと、
                  AIがあなたの一週間をまとめてお知らせ。
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(5) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[5] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑥"
            sub="大事な日記をしっかり守る！"
            title="充実のセキュリティ機能"
          />
          <div className="section-image-description-row">
            <img src={iconLock} className="left-image" alt="section1" />
            <div className="description-wrapper">
              <ul>
                <li>スクリーンロック機能で、他人に日記を覗かれる心配なし。</li>
                <li>SNSに日記を投稿する際に、個人名をAIが匿名化してくれる。</li>
                <li>サーバーとの通信は完全に暗号化。</li>
                <li>個人情報の入力は不要です。</li>
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(6) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[6] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑦"
            sub="機種変も安心！"
            title="日記は全て自動バックアップ"
          />
          <div className="section-image-description-row">
            <img src={iconBackup} className="left-image" alt="section1" />
            <div className="description-wrapper">
              <ul>
                <li>
                  記はサーバーに保存されるので、スマホを無くしたり、機種変しても日記が消えること
                  はありません
                </li>
                <li>
                  さらに、一括シェア機能を使えば、Google
                  Keepなどのアプリにも簡単に保存できます
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(7) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[7] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑧"
            sub="あの日の日記もすぐ見つかる！"
            title="豊富な検索機能"
          />
          <div className="section-image-description-row">
            <img src={iconSearch} className="left-image" alt="section1" />
            <div className="description-wrapper">
              <ul>
                <li>
                  記はサーバーに保存されるので、スマホを無くしたり、機種変しても日記が消えること
                  はありません
                </li>
                <li>
                  さらに、一括シェア機能を使えば、Google
                  Keepなどのアプリにも簡単に保存できます
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(8) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[8] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑨"
            sub="無料で使える！"
            title="基本機能は全て無料！"
          />
          <div className="section-image-description-row">
            <img src={iconFree} className="left-image" alt="section1" />
            <div className="description-wrapper">
              <ul>
                <li>
                  日記の作成、AIとの会話、画像の生成など、基本機能は全て無料で使えます。
                </li>
                <li>
                  日記も毎日5個書けるので、無料でもずっと使い続けられます。
                </li>
                <li>
                  更にヘビーに使いたいユーザー向けのプレミアムプランも用意しています。
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`section scroll-animate ${
            visibleSections.has(9) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[9] = el)}
        >
          <TitleLabel title="ご利用に際しての注意点" />
          <div className="description-wrapper">
            <ul>
              <li>
                本アプリは日記の生成にAIを使いますが、AIが出力する情報は必ずしも正しいとは限りません。必ず出力内容をご確認の上、日記の保存、公開を行ってください。
              </li>
              <li>
                必ず利用規約とプライバシーポリシーをご確認の上、同意してからご利用ください。
              </li>
            </ul>
          </div>
          <div className="annotation-wrapper">
            <ul>
              <li>
                ※1.サービス終了やメンテナンスなどの理由でサーバーが停止する可能性があります。ご了承ください。（シェア機能を使って定期的に外部サービスに日記をシェアしておくことをおすすめします）
              </li>
              {/* <li>
              ※2.2024/9/1現在は完全無料ですが、今後、有料版機能を提供する可能性はあります。
            </li> */}
            </ul>
          </div>
          {tutorial !== "true" && (
            <div className="tosppWrapper">
              <Link to="/tos">利用規約</Link>・
              <Link to="/privacy">プライバシーポリシー</Link>・
              <Link to="/deletedata">データの削除方法</Link>
            </div>
          )}
          {tutorial !== "true" && (
            <div className="store-image-wrapper">
              <img
                src={googlePlayBanner}
                onClick={() => {
                  window.open(GOOGLE_PLAY_URL, "_blank");
                }}
                className="google-playbanner-image"
                alt="google play"
              />
              <img
                src={appStoreBanner}
                onClick={() => {
                  window.open(APP_STORE_URL, "_blank");
                }}
                className="google-playbanner-image"
                alt="google play"
              />
            </div>
          )}
        </div>
        {tutorial !== "true" && (
          <div
            className={`section scroll-animate ${
              visibleSections.has(10) ? "animate-in" : ""
            }`}
            ref={(el) => (sectionRefs.current[10] = el)}
          >
            <TitleLabel title="お問合せ先" />

            <div className="description-wrapper">
              <a href="mailto:support@ai-daiary.net">support@ai-daiary.net</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type TitleLabelProps = {
  label?: string;
  number?: string;
  sub?: string;
  title: string;
};
const TitleLabel = (props: TitleLabelProps) => {
  return (
    <div className="section-title-wrapper">
      {props.label && (
        <p className="feature-label">
          {props.label}
          <span>{props.number}</span>
        </p>
      )}
      <div className="section-title-right-wrapper">
        {props.sub && <p className="section-title-sub">{props.sub}</p>}
        <p className="title">{props.title}</p>
      </div>
    </div>
  );
};

export default TopPage;
